import { motion } from "framer-motion";
import { ArrowRight, BookOpen } from "lucide-react";
import MolecularBackground from "@/components/landing/MolecularBackground";
import AbstractShapes from "@/components/landing/AbstractShapes";
import LiveStats from "@/components/landing/LiveStats";

const STATS = [
  { label: "Founded", value: "2018", sub: "Sofia · Bulgaria" },
  { label: "Journals", value: "02", sub: "International peer-reviewed" },
  { label: "Authors", value: "Global", sub: "Across 40+ countries" },
  { label: "Access", value: "Open", sub: "CC-BY licensing" },
];

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] overflow-hidden bg-[var(--sci-bg)]"
    >
      {/* Background image fallback layer */}
      <div
        className="absolute inset-0 opacity-[0.16] pointer-events-none"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxtb2xlY3VsYXIlMjBhYnN0cmFjdCUyMG5ldHdvcmt8ZW58MHx8fHwxNzgyMDc1MDYzfDA&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "saturate(0.7)",
        }}
      />
      {/* Soft radial wash for legibility */}
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-white/55 via-white/30 to-white" />

      {/* Animated molecular network */}
      <div className="absolute inset-0">
        <MolecularBackground density={1.1} />
      </div>

      {/* Abstract overlay sigil + arcs for editorial-art feel */}
      <AbstractShapes
        variant="rings"
        tone="ink"
        className="hidden md:block right-[-180px] top-[5%] w-[640px] h-[640px] opacity-60"
      />
      <AbstractShapes
        variant="arcs"
        tone="ink"
        className="hidden md:block left-[-100px] bottom-[-80px] w-[420px] h-[420px] opacity-50"
      />

      {/* Side ornaments */}
      <div className="hidden md:block absolute left-6 top-1/2 -translate-y-1/2 overline text-[var(--sci-text-muted)] [writing-mode:vertical-rl] rotate-180 pointer-events-none">
        ISSN · 2534-9821  /  E-ISSN · 2367-699X
      </div>
      <div className="hidden md:block absolute right-6 top-1/2 -translate-y-1/2 overline text-[var(--sci-text-muted)] [writing-mode:vertical-rl] pointer-events-none">
        Sofia · Bulgaria
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 md:pt-40 pb-20 md:pb-28">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="max-w-4xl"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="inline-block w-10 h-px bg-[var(--sci-teal)]" />
            <span className="overline text-[var(--sci-teal)]">
              International Academic Publisher · Est. 2018
            </span>
          </div>

          <h1
            data-testid="hero-headline"
            className="font-serif-display tracking-tight leading-[0.95] text-[var(--sci-navy)] text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem]"
          >
            Publishing <em className="not-italic text-[var(--sci-teal)] font-serif-display">Medical Research</em>
            <br />
            that Matters.
          </h1>

          <p
            data-testid="hero-subheadline"
            className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-[var(--sci-text)]/80"
          >
            International Sci Ink Press Ltd is an independent academic publisher
            dedicated to advancing medical knowledge through rigorous peer review,
            open access publishing, and global scientific collaboration.
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="#journals"
              data-testid="hero-cta-journals"
              className="btn-primary inline-flex items-center gap-2 px-7 py-3.5 text-sm tracking-wide"
            >
              <BookOpen size={16} />
              View Our Journals
            </a>
            <a
              href="#submit"
              data-testid="hero-cta-submit"
              className="btn-secondary inline-flex items-center gap-2 px-7 py-3.5 text-sm tracking-wide"
            >
              Submit Your Research
              <ArrowRight size={16} />
            </a>
          </div>
        </motion.div>

        {/* Stats bar - glassmorphism (live from Crossref) */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.4, ease: "easeOut" }}
          className="relative mt-20 md:mt-28"
        >
          <LiveStats />
        </motion.div>
      </div>
    </section>
  );
}
